import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Newspaper, Pencil, Plus, Trash2, Sparkles } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";

const inputCls =
  "w-full bg-ink border border-line text-mist px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300 placeholder:text-dim/50";

const EMPTY = { title: "", excerpt: "", date: "", read_time: "4 мин", body: "", cover: "", title_en: "", excerpt_en: "", read_time_en: "", body_en: "" };

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block font-mono text-[11px] uppercase tracking-[0.25em] text-dim mb-2">
    {children}
  </label>
);

const LangTabs = ({ value, onChange, hasEn }) => (
  <div className="flex border border-line w-max" role="tablist" data-testid="article-lang-tabs">
    {[
      { id: "bg", label: "Български" },
      { id: "en", label: hasEn ? "English ✓" : "English" },
    ].map((l) => (
      <button
        key={l.id}
        type="button"
        role="tab"
        aria-selected={value === l.id}
        data-testid={`article-lang-tab-${l.id}`}
        onClick={() => onChange(l.id)}
        className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-mono transition-colors duration-300 ${value === l.id ? "bg-gold text-ink" : "text-dim hover:text-mist"}`}
      >
        {l.label}
      </button>
    ))}
  </div>
);

export default function AdminArticles() {
  const [articles, setArticles] = useState(null);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [langTab, setLangTab] = useState("bg");
  const navigate = useNavigate();

  const translate = async () => {
    if (form.title.trim().length < 3 || form.body.trim().length < 10) {
      setLangTab("bg");
      toast.error("Първо попълнете българското заглавие и текст.");
      return;
    }
    if (form.title_en.trim() && !window.confirm("Английската версия ще бъде презаписана от AI превода. Продължавате ли?")) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/admin/articles/translate", {
        title: form.title,
        excerpt: form.excerpt,
        read_time: form.read_time,
        body: form.body,
      });
      setForm((f) => ({ ...f, ...data }));
      setLangTab("en");
      toast.success("Английската версия е генерирана — прегледайте я и запазете.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTranslating(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/articles");
      setArticles(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("ra_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const openNew = () => {
    setFile(null);
    setLangTab("bg");
    setForm({
      ...EMPTY,
      date: new Date().toLocaleDateString("bg-BG", { day: "numeric", month: "long", year: "numeric" }),
    });
  };

  const openEdit = (a) => {
    setFile(null);
    setLangTab("bg");
    setForm({
      id: a.id,
      title: a.title,
      excerpt: a.excerpt,
      date: a.date,
      read_time: a.read_time,
      body: (a.body || []).join("\n\n"),
      cover: a.cover || "",
      title_en: a.title_en || "",
      excerpt_en: a.excerpt_en || "",
      read_time_en: a.read_time_en || "",
      body_en: (a.body_en || []).join("\n\n"),
    });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3 || form.body.trim().length < 10) {
      setLangTab("bg");
      toast.error("Попълнете българското заглавие (мин. 3 символа) и текст (мин. 10 символа).");
      return;
    }
    if (form.title_en.trim() && form.body_en.trim().length < 10) {
      setLangTab("en");
      toast.error("Английският текст трябва да е поне 10 символа или оставете английската версия празна.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        date: form.date,
        read_time: form.read_time,
        body: form.body,
        title_en: form.title_en,
        excerpt_en: form.excerpt_en,
        read_time_en: form.read_time_en,
        body_en: form.body_en,
      };
      let articleId = form.id;
      if (articleId) {
        await api.put(`/admin/articles/${articleId}`, payload);
      } else {
        const { data } = await api.post("/admin/articles", payload);
        articleId = data.id;
      }
      if (file && articleId) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/articles/${articleId}/image`, fd);
      }
      toast.success(form.id ? "Статията е обновена." : "Статията е публикувана.");
      setForm(null);
      setFile(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeImage = async () => {
    if (!form.id) return;
    try {
      await api.delete(`/admin/articles/${form.id}/image`);
      setForm((f) => ({ ...f, cover: "" }));
      setFile(null);
      toast.success("Снимката е премахната.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Сигурни ли сте, че искате да изтриете тази статия?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      setArticles((list) => list.filter((a) => a.id !== id));
      toast.success("Статията е изтрита.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-articles" className="min-h-screen bg-ink text-mist">
      <header className="border-b border-line bg-coal/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo.png" alt="RightAccount" className="h-9 w-auto" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-dim ml-4">Статии</span>
          </div>
          <Link
            to="/admin"
            data-testid="articles-back-link"
            className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs text-dim hover:text-gold hover:border-gold transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Към запитванията
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <p className="text-dim text-sm">
            {articles ? `${articles.length} публикувани статии` : "Зареждане…"}
          </p>
          <button
            data-testid="article-new-button"
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300"
          >
            <Plus size={15} /> Нова статия
          </button>
        </div>

        {form && (
          <form data-testid="article-form" onSubmit={save} noValidate className="border border-gold/40 bg-coal p-6 md:p-8 mb-10 grid gap-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <LangTabs value={langTab} onChange={setLangTab} hasEn={!!form.title_en.trim()} />
                <button
                  type="button"
                  data-testid="article-translate-button"
                  onClick={translate}
                  disabled={translating || saving}
                  className="inline-flex items-center gap-2 border border-gold/60 text-gold px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-mono hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait"
                >
                  <Sparkles size={14} className={translating ? "animate-pulse" : ""} />
                  {translating ? "Превеждане…" : "Преведи с AI"}
                </button>
              </div>
              <p className="text-dim/70 text-xs max-w-md">
                {langTab === "bg"
                  ? "Българската версия е задължителна и се показва по подразбиране."
                  : "Английската версия е по избор — показва се, когато посетителят избере EN. Ако е празна, се показва българската."}
              </p>
            </div>

            <div className={langTab === "bg" ? "grid gap-5" : "hidden"} data-testid="article-fields-bg">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="af-title">Заглавие *</Label>
                  <input id="af-title" data-testid="article-title-input" required minLength={3} value={form.title} onChange={set("title")} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="af-date">Дата</Label>
                    <input id="af-date" data-testid="article-date-input" value={form.date} onChange={set("date")} className={inputCls} />
                  </div>
                  <div>
                    <Label htmlFor="af-read">Време за четене</Label>
                    <input id="af-read" data-testid="article-readtime-input" value={form.read_time} onChange={set("read_time")} className={inputCls} placeholder="4 мин" />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="af-excerpt">Кратко описание</Label>
                <input id="af-excerpt" data-testid="article-excerpt-input" value={form.excerpt} onChange={set("excerpt")} className={inputCls} />
              </div>
              <div>
                <Label htmlFor="af-body">
                  Текст * <span className="normal-case tracking-normal text-dim/60">(всеки абзац на нов ред)</span>
                </Label>
                <textarea id="af-body" data-testid="article-body-input" required minLength={10} rows={10} value={form.body} onChange={set("body")} className={`${inputCls} resize-y font-sans leading-relaxed`} />
              </div>
            </div>

            <div className={langTab === "en" ? "grid gap-5" : "hidden"} data-testid="article-fields-en">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="af-title-en">Title (EN)</Label>
                  <input id="af-title-en" data-testid="article-title-en-input" value={form.title_en} onChange={set("title_en")} className={inputCls} placeholder="English title" />
                </div>
                <div>
                  <Label htmlFor="af-read-en">Reading time (EN)</Label>
                  <input id="af-read-en" data-testid="article-readtime-en-input" value={form.read_time_en} onChange={set("read_time_en")} className={inputCls} placeholder="4 min" />
                </div>
              </div>
              <div>
                <Label htmlFor="af-excerpt-en">Excerpt (EN)</Label>
                <input id="af-excerpt-en" data-testid="article-excerpt-en-input" value={form.excerpt_en} onChange={set("excerpt_en")} className={inputCls} />
              </div>
              <div>
                <Label htmlFor="af-body-en">
                  Body (EN) <span className="normal-case tracking-normal text-dim/60">(one paragraph per line)</span>
                </Label>
                <textarea id="af-body-en" data-testid="article-body-en-input" rows={10} value={form.body_en} onChange={set("body_en")} className={`${inputCls} resize-y font-sans leading-relaxed`} />
              </div>
            </div>
            <div>
              <Label>Заглавна снимка (JPG, PNG или WebP, до 8 МБ)</Label>
              {(file || form.cover) && (
                <img
                  data-testid="article-image-preview"
                  src={file ? URL.createObjectURL(file) : `${API}/files/${form.cover}`}
                  alt="Преглед на снимката"
                  className="w-full max-w-md aspect-[16/9] object-cover border border-line mb-3"
                />
              )}
              <div className="flex items-center gap-4 flex-wrap">
                <label data-testid="article-image-label" className="inline-flex items-center gap-2 border border-line text-dim px-5 py-3 text-sm cursor-pointer hover:text-gold hover:border-gold transition-colors duration-300">
                  <ImagePlus size={15} />
                  {file ? file.name : form.cover ? "Сменете снимката" : "Качете снимка"}
                  <input
                    data-testid="article-image-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
                {(file || form.cover) && (
                  <button
                    data-testid="article-image-remove-button"
                    type="button"
                    onClick={file ? () => setFile(null) : removeImage}
                    className="inline-flex items-center gap-2 text-dim text-sm hover:text-red-400 transition-colors duration-300"
                  >
                    <Trash2 size={14} /> Премахнете
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                data-testid="article-save-button"
                type="submit"
                disabled={saving}
                className="bg-gold text-ink px-7 py-3 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
              >
                {saving ? "Записване…" : form.id ? "Запазете промените" : "Публикувайте"}
              </button>
              <button
                data-testid="article-cancel-button"
                type="button"
                onClick={() => { setForm(null); setFile(null); }}
                className="border border-line text-dim px-7 py-3 text-sm hover:text-mist hover:border-mist transition-colors duration-300"
              >
                Откажете
              </button>
            </div>
          </form>
        )}

        {articles === null ? (
          <p className="text-dim text-sm" data-testid="articles-loading">Зареждане…</p>
        ) : articles.length === 0 ? (
          <div data-testid="articles-empty" className="border border-line bg-coal p-16 text-center">
            <Newspaper size={28} className="text-dim mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-dim text-sm">Все още няма статии. Създайте първата.</p>
          </div>
        ) : (
          <div data-testid="articles-admin-list" className="border border-line bg-coal divide-y divide-line">
            {articles.map((a) => (
              <div key={a.id} data-testid={`article-row-${a.id}`} className="flex items-center gap-5 px-5 md:px-6 py-5 hover:bg-ink/60 transition-colors duration-200">
                {a.cover && (
                  <img src={`${API}/files/${a.cover}`} alt="" className="w-20 h-12 object-cover border border-line shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-mist font-medium truncate">{a.title}</p>
                  <p className="font-mono text-[11px] text-dim mt-1">
                    {a.date} · {a.read_time} · {(a.body || []).length} абзаца
                    {a.title_en ? (
                      <span data-testid={`article-en-badge-${a.id}`} className="ml-3 text-gold">· EN ✓</span>
                    ) : (
                      <span data-testid={`article-en-missing-${a.id}`} className="ml-3 text-dim/50">· без EN</span>
                    )}
                  </p>
                </div>
                <button
                  data-testid={`article-edit-${a.id}`}
                  onClick={() => openEdit(a)}
                  className="text-dim hover:text-gold transition-colors duration-200 p-2"
                  aria-label="Редактирай"
                >
                  <Pencil size={16} />
                </button>
                <button
                  data-testid={`article-delete-${a.id}`}
                  onClick={() => remove(a.id)}
                  className="text-dim hover:text-red-400 transition-colors duration-200 p-2"
                  aria-label="Изтрий"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
